// ─────────────────────────────────────────────────────────
// Einstellungen → Buchhaltung & Rechnungswesen → Mahnungen
// ─────────────────────────────────────────────────────────
// plancraft-Modell: EINE Karte „Zahlungserinnerung" mit Bearbeiten-Button, darin
// drei Stufen untereinander — Zahlungserinnerung (nur Zahlungsziel), 1. Mahnung
// und 2. Mahnung (je + Verzugszinsen % + Mahngebühr). Bearbeiten läuft über ein
// Modal mit denselben Feldern.
//
// Beträge in CHF (plancraft zeigt €) — LevelUpWerk ist für die Schweiz.
// Prefix mh-. Rechte: accounting:read / accounting:write.
import { icon, escapeHtml } from '../../utils/dom.js';
import { saveDunning } from '../../core/org.js';
import { fetchDunningCandidates, sendDunning as sendDunningApi } from '../../core/dunning.js';
import { listWhatsAppInstances } from '../../core/whatsapp.js';
import { WHATSAPP_ENABLED } from '../../core/features.js';
import { money, percent, date } from '../../utils/format.ts';
import { t } from '../../i18n/index.js';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const days = (n) => `${num(n)} ${t(num(n) === 1 ? 'settings.dunning.day' : 'settings.dunning.days')}`;
const pct = (n) => percent(n);
const chf = (n) => money(n);

function data(s) {
  const d = s.dunning || {};
  return {
    reminder: d.reminder || { paymentDays: 0 },
    level1: d.level1 || { paymentDays: 0, interestPercent: 0, feeAmount: 0 },
    level2: d.level2 || { paymentDays: 0, interestPercent: 0, feeAmount: 0 },
  };
}

// ── Lese-Ansicht ──────────────────────────────────────────
const readRow = (label, value) => `
  <div class="cd-mh-row">
    <div class="cd-mh-cap">${escapeHtml(label)}</div>
    <div class="cd-mh-val">${escapeHtml(value)}</div>
  </div>`;

export function dunningHeadButton(ctx) {
  if (!ctx.can('accounting:write')) return '';
  return `<button class="cd-btn-ghost" data-action="mh-edit">${icon('edit', 15)}${escapeHtml(t('common.edit'))}</button>`;
}

export function renderDunningSection(ctx) {
  const { s, can } = ctx;
  if (!can('accounting:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (!s.dunning) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;
  const d = data(s);

  const stage = (title, lvl, withCharges) => `
    <div class="cd-mh-stage">
      <div class="cd-mh-stage-title">${escapeHtml(title)}</div>
      ${readRow(t('settings.dunning.paymentDays'), days(lvl.paymentDays))}
      ${withCharges ? readRow(t('settings.dunning.interest'), pct(lvl.interestPercent)) : ''}
      ${withCharges ? readRow(t('settings.dunning.fee'), chf(lvl.feeAmount)) : ''}
    </div>`;

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('receipt_long', 18)}<h3>${escapeHtml(t('settings.dunning.title'))}</h3></div>
        <div class="cd-mh-body">
          ${readRow(t('settings.dunning.paymentDays'), days(d.reminder.paymentDays))}
          ${stage(t('settings.dunning.level1'), d.level1, true)}
          ${stage(t('settings.dunning.level2'), d.level2, true)}
        </div>
      </div>
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.dunning.note'))}</span></div>
    </div>
    ${renderDunningCandidates({ s, can })}`;
}

// ── Fällige Mahnungen — macht listDunningCandidates sichtbar+nutzbar ──────
function stageBadgeLabel(stage) {
  return t('settings.dunning.candidates.stage' + stage);
}

function renderDunningCandidates({ s, can }) {
  if (!s.dunningCandidates) return '';
  const rows = s.dunningCandidates;
  const canSend = can('accounting:write');

  if (!rows.length) {
    return `
      <div class="cd-emp-card" style="margin-top:16px;">
        <div class="cd-emp-card-head">${icon('list_alt', 18)}<h3>${escapeHtml(t('settings.dunning.candidates.title'))}</h3></div>
        <div class="cd-set-empty">${escapeHtml(t('settings.dunning.candidates.empty'))}</div>
      </div>`;
  }

  const trs = rows.map((c) => {
    const lastSent = c.lastSent
      ? escapeHtml(t('settings.dunning.candidates.sentOn', { date: date(c.lastSent.at), stage: stageBadgeLabel(c.lastSent.stage) }))
      : escapeHtml(t('settings.dunning.candidates.neverSent'));
    return `
      <tr>
        <td>${escapeHtml(c.number)}</td>
        <td>${escapeHtml(c.clientName)}</td>
        <td><span class="cd-mh-badge">${escapeHtml(stageBadgeLabel(c.stage))}</span></td>
        <td>${num(c.daysOverdue)}</td>
        <td>${escapeHtml(chf(c.feeAmount))}</td>
        <td>${escapeHtml(chf(c.interestAmount))}</td>
        <td>${escapeHtml(chf(c.claimTotal))}</td>
        <td class="cd-mh-lastsent">${lastSent}</td>
        <td>${canSend ? `<button class="cd-btn-ghost" data-action="mh-send-open" data-id="${escapeHtml(c.id)}">${escapeHtml(t('settings.dunning.candidates.sendButton'))}</button>` : ''}</td>
      </tr>`;
  }).join('');

  return `
    <div class="cd-emp-card" style="margin-top:16px;">
      <div class="cd-emp-card-head">${icon('list_alt', 18)}<h3>${escapeHtml(t('settings.dunning.candidates.title'))}</h3></div>
      <table class="cd-mh-table">
        <thead><tr>
          <th>${escapeHtml(t('settings.dunning.candidates.colInvoice'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colCustomer'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colStage'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colDaysOverdue'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colFee'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colInterest'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colClaimTotal'))}</th>
          <th>${escapeHtml(t('settings.dunning.candidates.colLastSent'))}</th>
          <th></th>
        </tr></thead>
        <tbody>${trs}</tbody>
      </table>
    </div>`;
}

// ── Mahnung senden — Modal (eigenständig, orientiert an cd-send-modal) ────
export function renderDunningSendModal({ s }) {
  const m = s.mhSend;
  if (!m) return '';
  const okBox = m.ok ? `<div class="cd-ok">${icon('check', 16)}${escapeHtml(t('settings.dunning.sendModal.sentOk'))}</div>` : '';
  const errBox = m.error ? `<div class="cd-form-err">${escapeHtml(m.error)}</div>` : '';

  const emailPane = `
    <label class="cd-send-label">${escapeHtml(t('settings.dunning.sendModal.recipientEmail'))}</label>
    <input class="cd-send-input" type="email" placeholder="kunde@example.ch" value="${escapeHtml(m.toEmail)}" data-mh-field="toEmail" />
    <label class="cd-send-label">${escapeHtml(t('dashboard.sendModal.subject'))}</label>
    <input class="cd-send-input" type="text" placeholder="${escapeHtml(t('dashboard.sendModal.automatic'))}" value="${escapeHtml(m.subject)}" data-mh-field="subject" />
    <label class="cd-send-label">${escapeHtml(t('dashboard.sendModal.message'))}</label>
    <textarea class="cd-send-input cd-send-area" rows="4" placeholder="${escapeHtml(t('dashboard.sendModal.automaticText'))}" data-mh-field="message">${escapeHtml(m.message)}</textarea>`;

  let waPane;
  if (!s.mhWaInstances || !s.mhWaInstances.length) {
    waPane = `<div class="cd-send-empty">${escapeHtml(t('dashboard.sendModal.noWaNumber'))}</div>`;
  } else {
    const opts = s.mhWaInstances.map((i) =>
      `<option value="${escapeHtml(i.id)}"${i.id === m.instanceId ? ' selected' : ''}>${escapeHtml(i.label || i.phoneNumber || i.id)}</option>`
    ).join('');
    waPane = `
      <label class="cd-send-label">${escapeHtml(t('settings.dunning.sendModal.waInstance'))}</label>
      <select class="cd-send-input" data-mh-field="instanceId"><option value="">${escapeHtml(t('dashboard.sendModal.choosePlaceholder'))}</option>${opts}</select>
      <label class="cd-send-label">${escapeHtml(t('settings.dunning.sendModal.recipientPhone'))}</label>
      <input class="cd-send-input" type="text" placeholder="41791234567" value="${escapeHtml(m.toPhone)}" data-mh-field="toPhone" />
      <label class="cd-send-label">${escapeHtml(t('dashboard.sendModal.messageOptional'))}</label>
      <textarea class="cd-send-input cd-send-area" rows="4" placeholder="${escapeHtml(t('dashboard.sendModal.automaticText'))}" data-mh-field="message">${escapeHtml(m.message)}</textarea>`;
  }

  const activePane = m.channel === 'whatsapp' ? waPane : emailPane;

  return `
    <div class="cd-send-modal">
      <div class="cd-send-backdrop" data-action="mh-send-cancel"></div>
      <div class="cd-send-card">
        <div class="cd-send-head">
          <h3>${escapeHtml(t('settings.dunning.sendModal.title', { number: m.number }))}</h3>
          <button class="cd-bell" data-action="mh-send-cancel">${icon('close', 18)}</button>
        </div>
        <div class="cd-send-tabs">
          <button class="cd-send-tab ${m.channel === 'email' ? 'active' : ''}" data-action="mh-send-tab-email">${icon('mail', 16)}${escapeHtml(t('dashboard.nav.email'))}</button>
          ${WHATSAPP_ENABLED ? `<button class="cd-send-tab ${m.channel === 'whatsapp' ? 'active' : ''}" data-action="mh-send-tab-whatsapp">${icon('chat', 16)}${escapeHtml(t('dashboard.nav.whatsapp'))}</button>` : ''}
        </div>
        ${okBox}${errBox}
        ${activePane}
        <div class="cd-send-foot">
          <button class="cd-btn-ghost" data-action="mh-send-cancel">${escapeHtml(t('common.cancel'))}</button>
          <button class="cd-btn-primary" data-action="mh-send-submit"${m.busy ? ' disabled' : ''}>${escapeHtml(m.busy ? t('dashboard.sendModal.sending') : t('settings.dunning.sendModal.send'))}</button>
        </div>
      </div>
    </div>`;
}

// ── Modal ─────────────────────────────────────────────────
const field = (label, id, value, suffix, step) => `
  <div class="cd-dt-field">
    <label class="cd-dt-cap" for="${id}">${escapeHtml(label)}</label>
    <div class="cd-lh-mm">
      <input class="cd-input" id="${id}" type="number" min="0" step="${step}" value="${escapeHtml(String(value ?? ''))}">
      <span class="cd-lh-mm-unit">${escapeHtml(suffix)}</span>
    </div>
  </div>`;

export function renderDunningModal({ s }) {
  const e = s.mhEdit;
  if (!e) return '';
  const d = e.data;
  const stage = (title, key) => `
    <div class="cd-lh-group">
      <div class="cd-lh-grouphead"><div class="cd-dt-cap">${escapeHtml(title)}</div></div>
      <div class="cd-mh-grid">
        ${field(t('settings.dunning.paymentDaysField'), `mh-${key}-days`, d[key].paymentDays, t('settings.dunning.days'), '1')}
        ${field(t('settings.dunning.interestField'), `mh-${key}-int`, d[key].interestPercent, '%', '0.01')}
        ${field(t('settings.dunning.feeField'), `mh-${key}-fee`, d[key].feeAmount, 'CHF', '0.05')}
      </div>
    </div>`;

  return `
    <div class="cd-modal-overlay" data-action="mh-cancel">
      <div class="cd-modal cd-lh-modal wide" data-stop>
        <div class="cd-modal-head">
          <div class="cd-modal-title">${icon('receipt_long', 18)}<h3>${escapeHtml(t('settings.dunning.modalTitle'))}</h3></div>
          <div class="cd-modal-actions">
            <button class="cd-btn-ghost" data-action="mh-cancel">${escapeHtml(t('common.cancel'))}</button>
            <button class="cd-btn-primary" data-action="mh-save" ${s.mhSaving ? 'disabled' : ''}>${escapeHtml(s.mhSaving ? t('common.saving') : t('common.save'))}</button>
          </div>
        </div>
        <div class="cd-modal-body">
          <div class="cd-lh-group">
            <div class="cd-lh-grouphead"><div class="cd-dt-cap">${escapeHtml(t('settings.dunning.reminderCaps'))}</div></div>
            ${field(t('settings.dunning.paymentDaysField'), 'mh-reminder-days', d.reminder.paymentDays, t('settings.dunning.days'), '1')}
          </div>
          ${stage(t('settings.dunning.level1Caps'), 'level1')}
          ${stage(t('settings.dunning.level2Caps'), 'level2')}
        </div>
      </div>
    </div>`;
}

// Eingaben vor jedem Redraw sichern (sonst gehen sie beim draw() verloren)
export function syncFromDom(s) {
  const e = s.mhEdit;
  if (e) {
    const v = (id) => { const el = document.getElementById(id); return el ? el.value : undefined; };
    const set = (obj, key, id) => { const x = v(id); if (x !== undefined) obj[key] = x; };
    set(e.data.reminder, 'paymentDays', 'mh-reminder-days');
    for (const k of ['level1', 'level2']) {
      set(e.data[k], 'paymentDays', `mh-${k}-days`);
      set(e.data[k], 'interestPercent', `mh-${k}-int`);
      set(e.data[k], 'feeAmount', `mh-${k}-fee`);
    }
  }
  if (s.mhSend) {
    // toEmail/toPhone sind je EIGENE data-mh-field-Attribute (nicht ein
    // gemeinsames "to") — nur das Feld der gerade sichtbaren Pane existiert im
    // DOM, das andere bleibt unangetastet. Ein Channel-abhängiges Ziel-Feld hier
    // auszurechnen wäre riskant: draw() ruft diese Funktion VOR dem Neuzeichnen
    // auf, während im DOM noch die ALTE Pane steht, s.mhSend.channel aber schon
    // den NEUEN Tab zeigt — bei gemeinsamem Feldnamen würde das den falschen
    // Wert in den falschen Kanal schreiben.
    for (const field of ['toEmail', 'toPhone', 'subject', 'message', 'instanceId']) {
      const el = document.querySelector(`[data-mh-field="${field}"]`);
      if (el) s.mhSend[field] = el.value;
    }
  }
}

export function handleDunningAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  if (!a || a.indexOf('mh-') !== 0) return false;

  if (a === 'mh-edit') {
    if (!ctx.can('accounting:write')) return true;
    const d = data(s);
    // tiefe Kopie — Abbrechen darf den geladenen Zustand nicht verändern
    s.mhEdit = { data: JSON.parse(JSON.stringify(d)) };
    draw();
    return true;
  }
  if (a === 'mh-cancel') { s.mhEdit = null; draw(); return true; }
  if (a === 'mh-save') {
    syncFromDom(s);
    const d = s.mhEdit.data;
    s.mhSaving = true; draw();
    saveDunning({
      reminder: { paymentDays: num(d.reminder.paymentDays) },
      level1: { paymentDays: num(d.level1.paymentDays), interestPercent: num(d.level1.interestPercent), feeAmount: num(d.level1.feeAmount) },
      level2: { paymentDays: num(d.level2.paymentDays), interestPercent: num(d.level2.interestPercent), feeAmount: num(d.level2.feeAmount) },
    })
      .then(() => { s.mhSaving = false; s.mhEdit = null; return reload ? reload() : null; })
      .then(() => draw())
      .catch((err) => { s.mhSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }

  // ── Mahnung senden ──
  if (a === 'mh-send-open') {
    if (!ctx.can('accounting:write')) return true;
    const id = el && el.dataset ? el.dataset.id : null;
    const c = (s.dunningCandidates || []).find((x) => x.id === id);
    if (!c) return true;
    s.mhSend = {
      // WhatsApp pausiert: Mahnungen gehen dann ausschliesslich per E-Mail —
      // für sie gibt es keine öffentliche Seite, also auch keinen Link-Weg.
      id: c.id, number: c.number, channel: (c.contactEmail || !WHATSAPP_ENABLED) ? 'email' : 'whatsapp',
      // getrennt je Kanal (nicht ein gemeinsames "to") — sonst würde beim
      // Tab-Wechsel die E-Mail im Telefon-Feld stehen bleiben oder umgekehrt.
      toEmail: c.contactEmail || '', toPhone: c.contactPhone || '',
      subject: '', message: '', instanceId: null,
      busy: false, error: '', ok: false,
    };
    s.mhWaInstances = null;
    draw();
    return true;
  }
  if (a === 'mh-send-cancel') { s.mhSend = null; draw(); return true; }
  if (a === 'mh-send-tab-email') { if (s.mhSend) { s.mhSend.channel = 'email'; draw(); } return true; }
  if (a === 'mh-send-tab-whatsapp') {
    if (s.mhSend) {
      s.mhSend.channel = 'whatsapp';
      draw();
      if (!s.mhWaInstances) loadMhWaInstances(s, draw);
    }
    return true;
  }
  if (a === 'mh-send-submit') {
    syncFromDom(s);
    const m = s.mhSend;
    if (!m) return true;
    const to = m.channel === 'whatsapp' ? m.toPhone : m.toEmail;
    if (!to || !to.trim()) { m.error = t('settings.dunning.sendModal.errRecipient'); draw(); return true; }
    if (m.channel === 'whatsapp' && !m.instanceId) { m.error = t('settings.dunning.sendModal.errInstance'); draw(); return true; }
    m.busy = true; m.error = ''; draw();
    sendDunningApi(m.id, {
      channel: m.channel,
      to: to.trim(),
      instanceId: m.instanceId || undefined,
      subject: m.subject || undefined,
      message: m.message || undefined,
    })
      .then(() => {
        m.busy = false; m.ok = true; draw();
        setTimeout(() => { s.mhSend = null; draw(); }, 1500);
        return reload ? reload() : null;
      })
      .catch((err) => { m.busy = false; m.error = (err && err.message) || t('settings.dunning.sendModal.errSend'); draw(); });
    return true;
  }
  return false;
}

async function loadMhWaInstances(s, draw) {
  try {
    const res = await listWhatsAppInstances();
    const list = Array.isArray(res) ? res : (res && res.instances) || [];
    s.mhWaInstances = Array.isArray(list) ? list : [];
    if (s.mhSend && !s.mhSend.instanceId && s.mhWaInstances[0]) s.mhSend.instanceId = s.mhWaInstances[0].id;
  } catch { s.mhWaInstances = []; }
  draw();
}
